<ng-template let-extraClass="extraClass" #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="'Upsell Configuration'"
      [canGoBack]="true" />
  }
</ng-template>

<ng-container
  *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper [class]="isRightSideContent ? 'right-side-scroll-enabled' : ''">
    @if (errorMessage) {
      <tidy-error-message
        [text]="errorMessage" />
    }

    @if (!isDataLoaded) {
      <div style="width: 100%">
        <ion-skeleton-text
          animated="true"
          style="height: 75px; border-radius: 6px" />
      </div>
      <div style="width: 100%; padding-top: 1rem;">
        <ion-skeleton-text
          animated="true"
          style="height: 200px; border-radius: 6px" />
      </div>
    }

    @if (isDataLoaded && guidebookConfig) {
      <tidy-row>
        <tidy-text
          [header]="guidebookConfig.property_name || 'Property'"
          [translate]="false" />
      </tidy-row>

      <tidy-row>
        <tidy-text
          [helper]="'Configure upsells and fees that guests see in the digital guidebook for this property.'" />
      </tidy-row>

      <!-- Waivo Damage Protection Section -->
      <tidy-card>
        <tidy-row [alignLastItemRight]="true">
          <div>
            <tidy-row>
              <tidy-image
                [src]="'assets/svg/shield-checkmark.svg'"
                class="body-size" />
              <tidy-text
                [header]="'Waivo Damage Protection'" />
            </tidy-row>
            <tidy-row>
              <tidy-text
                [body]="'Optional damage protection for guests. Covers accidental damage during their stay, replacing traditional security deposits.'" />
            </tidy-row>
          </div>
          <tidy-toggle
            [toggled]="waivoEnabled"
            (toggleChanged)="waivoEnabled = !waivoEnabled" />
        </tidy-row>
        @if (waivoEnabled) {
          <tidy-row>
            <tidy-text
              [helper]="'When enabled, guests will be offered the option to purchase damage protection at checkout. Pricing is managed by Waivo.'" />
          </tidy-row>
        }
        <tidy-row>
          <tidy-button
            [text]="'Save'"
            [action]="boundSaveWaivoConfig"
            class="primary" />
        </tidy-row>
      </tidy-card>

      <!-- Viator Affiliate Program Section -->
      <tidy-card>
        <tidy-row [alignLastItemRight]="true">
          <div>
            <tidy-row>
              <tidy-image
                [src]="'assets/svg/compass-outline.svg'"
                class="body-size" />
              <tidy-text
                [header]="'Viator Experiences'" />
            </tidy-row>
            <tidy-row>
              <tidy-text
                [body]="'Recommend local tours and experiences to your guests through the Viator affiliate program. Earn commission on bookings.'" />
            </tidy-row>
          </div>
          <tidy-toggle
            [toggled]="viatorEnabled"
            (toggleChanged)="viatorEnabled = !viatorEnabled" />
        </tidy-row>
        @if (viatorEnabled) {
          <tidy-row>
            <tidy-text
              [helper]="'Enabled by default. Guests will see recommended local experiences in the guidebook. You earn affiliate commissions on any bookings.'" />
          </tidy-row>
        }
        <tidy-row>
          <tidy-button
            [text]="'Save'"
            [action]="boundSaveViatorConfig"
            class="primary" />
        </tidy-row>
      </tidy-card>

      <!-- Custom Fees Section -->
      <tidy-card>
        <tidy-row>
          <tidy-image
            [src]="'assets/svg/cash-outline.svg'"
            class="body-size" />
          <tidy-text
            [header]="'Custom Fees'" />
        </tidy-row>
        <tidy-row>
          <tidy-text
            [body]="'Add custom fees guests can pay through the guidebook, such as pet fees, early check-in, late check-out, and more.'" />
        </tidy-row>

        @if (customFees.length > 0) {
          <tidy-divider />
          @for (fee of customFees; track fee.id) {
            <tidy-row [alignLastItemRight]="true" class="extra-top-padding">
              <div>
                <tidy-row>
                  <tidy-text
                    [title]="fee.name"
                    [translate]="false" />
                </tidy-row>
                <tidy-row>
                  <tidy-text
                    [helper]="fee.description"
                    [translate]="false" />
                </tidy-row>
                <tidy-row>
                  <tidy-text
                    [body]="formatPrice(fee)"
                    [translate]="false" />
                  @if (!fee.is_optional) {
                    <tidy-text
                      [helper]="' (Required)'" />
                  }
                </tidy-row>
              </div>
              <div>
                <tidy-toggle
                  [toggled]="fee.enabled"
                  (toggleChanged)="toggleCustomFee(fee)" />
              </div>
            </tidy-row>
            <tidy-row class="extra-top-padding">
              <tidy-text
                [actionBody]="'Remove'"
                (action)="removeCustomFee(fee)"
                class="link" />
            </tidy-row>
          }
        }

        <tidy-divider />

        @if (!showAddCustomFee) {
          <tidy-row class="extra-top-padding">
            <tidy-text
              [actionBody]="'+ Add Custom Fee'"
              (action)="toggleAddCustomFee()"
              class="link" />
          </tidy-row>

          @if (customFeeTemplates.length > 0) {
            <tidy-row class="extra-top-padding">
              <tidy-text
                [helper]="'Quick add from templates:'" />
            </tidy-row>
            <tidy-row>
              @for (template of customFeeTemplates; track template.name) {
                <tidy-text
                  [actionBody]="template.name"
                  (action)="prefillFromTemplate(template)"
                  class="link" />
              }
            </tidy-row>
          }
        }

        @if (showAddCustomFee) {
          <form [formGroup]="customFeeForm" class="extra-top-padding">
            <tidy-row>
              <tidy-text [title]="'New Custom Fee'" />
            </tidy-row>
            <tidy-row>
              <tidy-input
                [label]="'Fee Name'"
                formControlName="name"
                [form]="customFeeForm.controls.name"
                [icon]="'assets/svg/create-outline.svg'"
                [submitted]="submitted"
                [errorMessage]="'Fee name is required'" />
            </tidy-row>
            <tidy-row>
              <tidy-textarea
                [label]="'Description (optional)'"
                formControlName="description"
                [form]="customFeeForm.controls.description"
                [icon]="'assets/svg/information-circle.svg'" />
            </tidy-row>
            <tidy-row>
              <tidy-input
                [label]="'Price'"
                formControlName="price"
                [form]="customFeeForm.controls.price"
                [icon]="'assets/svg/cash-outline.svg'"
                [type]="'number'"
                [submitted]="submitted"
                [errorMessage]="'Price is required'" />
            </tidy-row>
            <tidy-row>
              <tidy-select
                [label]="'Price Type'"
                formControlName="price_type"
                [form]="customFeeForm.controls.price_type"
                [items]="priceTypeItems"
                [icon]="'assets/svg/pricetag-outline.svg'" />
            </tidy-row>
            <tidy-row>
              <tidy-checkbox
                [(checked)]="customFeeForm.controls.is_optional.value"
                [items]="[{viewValue: 'Optional for guest (guest can decline)', value: true}]" />
            </tidy-row>
            <tidy-row [align]="'center'">
              <tidy-button
                [text]="'Add Fee'"
                [action]="boundAddCustomFee"
                class="primary" />
            </tidy-row>
            <tidy-row [align]="'center'">
              <tidy-text
                [actionBody]="'Cancel'"
                (action)="toggleAddCustomFee()"
                class="link" />
            </tidy-row>
          </form>
        }
      </tidy-card>
    }
  </tidy-wrapper>
</ng-template>

@if (!isRightSideContent) {
  <app-scroll-wrapper [templateContent]="content" />
}

@if (isRightSideContent) {
  <ion-content>
    <ng-container *ngTemplateOutlet="content" />
  </ion-content>
}
